import { PageInfo } from "./components/Pagination";
import { FacilityGeneralManager } from "./features/facility/queries";
import { AuditEntry, SectionAuditEntry } from "./pages/sections/types";
import { Interaction, InteractionMode } from "@/components/interactions/types";

export type EnumOptions<T> = {
  enumValues: { name: T; description: string }[];
};

export type PersonAddressType = "home" | "secondary";
export type FacilityAddressType = "main" | "mailing";
export type AddressType = PersonAddressType | FacilityAddressType;

export interface Address {
  id: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  addressType: AddressType;
  isVerified: boolean;
}

export interface BackofficePerson {
  id: string;
  pgaId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  profilePhoto: string;
  primarySection: Section;
  sectionByHomeAddress?: Section;
  primarySectionAffiliation?: SectionAffiliation;
  primaryFacility?: Facility;
  latestProgram: Program;
  employments: Employment[];
  phoneNumbers: PhoneNumber[];
  addresses: Address[];
  email: string;
  contactAddressId: string;
  membershipSince: string;
  totalJobApplications: number;
  totalActiveJobApplications: number;
  // Additional fields for PersonSideSheet functionality
  age?: number;
  dob?: DateOfBirth;
  gender?: Gender;
  informalName?: string;
  preferredLastName?: string;
  spouseName?: string | null;
  groupAssignments?: GroupAssignment[];
  university?: { id: string; name: string; legacyId?: string };
  ccmsSuspensions?: CcmsSuspension[];
}

export interface Section {
  id: string;
  name: string;
  legacyId?: string;
  auditLog?: AuditEntry[];
  auditLogPagination?: {
    nodes: SectionAuditEntry[];
    pageInfo: PageInfo;
    totalCount: number;
  };
}

export interface Facility {
  id: string;
  name: string;
  section: Section;
  chapter?: { id: string; name: string } | null;
  addresses: Address[];
  mainAddress: Address;
  // ---
  mailingAddress?: Address | null;
  phone?: PhoneNumber | null;
  fax?: PhoneNumber | null;
  golfShopPhone?: PhoneNumber | null;
  website?: string | null;
  operatingStatus?: FacilityOperatingStatus | null;
  facilityType?: FacilityType;
  courseType?: FacilityCourseType | null;
  operationType?: FacilityOperationType | null;
  highestRankedEmployment?: {
    id: string;
    title: string;
    person: { id: string; displayName: string; informalName?: string };
  } | null;
  parentCompany?: { id: string; name: string } | null;
  generalManager?: FacilityGeneralManager | null;
  managementGroup?: string | null;
  lengthOfSeasonInMonths?: number | null;
  holesInRegulation?: number | null;
  practiceRangeAvailable?: boolean | null;
  simulatorAvailable?: boolean | null;
  golfCartBrand?: string | null;
  membershipTotal?: number | null;
  membershipMen?: number | null;
  membershipWomen?: number | null;
  membershipJunior?: number | null;
  membershipSenior?: number | null;
}

export type FacilityOperatingStatus = string & { __brand: "FacilityOperatingStatus" };
export type FacilityType = string & { __brand: "FacilityType" };
export type FacilityCourseType = string & { __brand: "FacilityCourseType" };
export type FacilityOperationType = string & { __brand: "FacilityOperationType" };

export enum WorkingLocation {
  ON_SITE = "ON_SITE",
  REMOTE = "REMOTE"
}

export interface Employment {
  id: string;
  title: string;
  formattedTitle?: string;
  startedOn: string | null;
  endedOn: string | null;
  isPrimary: boolean;
  facility: Facility;
  jobClassification: string | null;
  /** Present while classification is deferred until registration completes. */
  intendedJobClassification?: string | null;
  sectionApprovalDate?: string;
  notes?: CRMNote[];
}

export interface PhoneNumber {
  id: string;
  phoneType: "home" | "mobile" | "facility_phone" | "fax" | "golf_shop_phone";
  phoneNumber: string;
  phoneExtension?: string;
  phoneFormatted: string;
  phoneCountry: string;
}

export enum ProgramStatus {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  TERMINATED = "TERMINATED"
}

export const NotePriorityValues = {
  NONE: "NONE",
  PROMOTE: "PROMOTE",
  ALERT: "ALERT"
} as const;

export type NotePriority = (typeof NotePriorityValues)[keyof typeof NotePriorityValues] | null;

export const NoteVisibilityValues = {
  INTERNAL: "INTERNAL",
  EXTERNAL: "EXTERNAL"
} as const;

export type NoteVisibility = (typeof NoteVisibilityValues)[keyof typeof NoteVisibilityValues];

export const NoteNotableValues = {
  EMPLOYMENT: "EMPLOYMENT",
  EVENT: "EVENT",
  FACILITY: "FACILITY",
  FACILITY_ASSOCIATION: "FACILITY_ASSOCIATION",
  INTERACTION: "INTERACTION",
  MEMBERSHIP_APPLICATION: "MEMBERSHIP_APPLICATION",
  MEMBERSHIP_BAND: "MEMBERSHIP_BAND",
  MEMBERSHIP_CHANGE_REQUEST: "MEMBERSHIP_CHANGE_REQUEST",
  MEMBERSHIP_SUSPENSION: "MEMBERSHIP_SUSPENSION",
  EVENT_REGISTRATION: "EVENT_REGISTRATION",
  ORDER: "ORDER",
  PAYMENT: "PAYMENT",
  PERSON: "PERSON",
  UNIVERSITY_COHORT_ENROLLMENT: "UNIVERSITY_COHORT_ENROLLMENT",
  UNIVERSITY_PROBATION: "UNIVERSITY_PROBATION"
} as const;

export type NoteNotable = (typeof NoteNotableValues)[keyof typeof NoteNotableValues];

export interface CRMNoteReference {
  __typename: string;
  id: string;
  name: string;
  filename?: string;
  url?: string | null;
  category?: string;
  privacyRestricted?: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CRMNote {
  id: string;
  title: string | null;
  content: string;
  displayTitle: string | null;
  priority: NotePriority;
  priorityExpirationDate: string | null;
  visibility: NoteVisibility;
  notableId: string;
  notableType: string;
  eventSlug?: string | null;
  authorName?: string | null;
  createdAt: string;
  updatedAt: string | null;
  author?: { id: string; name?: string; email?: string } | null;
  references?: CRMNoteReference[];
  openTasksCount?: number;
}

export interface Band {
  id: string;
  expiresOn: string;
  startedOn: string;
  terminatesOn?: string;
  terminatedReason?: string;
  classification: Classification;
  sectionAffiliation?: SectionAffiliation;
  programType: ProgramType;
  createdReason?: string;
  section?: Section;
  notes?: CRMNote[];
}

interface Program {
  id: string;
  status: ProgramStatus;
  programType: ProgramType;
  band?: Band;
}

interface StatusWithDates {
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
  expirationDate?: string | null;
}

export interface BackgroundCheck extends StatusWithDates {}
export interface QualifyingLevel extends StatusWithDates {
  completed?: boolean;
  levelCode?: string | null;
}
export interface LatestAssociateApplication extends StatusWithDates {}

export interface MembershipApplicationSummary {
  id: string;
  applicationType: string;
  status: string;
  approvedAt?: string | null;
  electionDate?: {
    id: string;
    electionDate: string;
  } | null;
}

export interface EligibleEmployment extends StatusWithDates {
  id: string;
}

export interface ExperienceCreditSource {
  origin: string;
  sourceId: string | null;
  creditType: string | null;
  credits: number;
  description: string | null;
  startedOn: string | null;
  endedOn: string | null;
}

export interface ExperienceCreditSuspensionPeriod {
  code: string;
  startedOn: string | null;
  endedOn: string | null;
}

export interface ExperienceCredits {
  total: number;
  threshold?: number | null;
  complete?: boolean | null;
  employmentCredits: number;
  educationCredits: number;
  sources: ExperienceCreditSource[];
  excludedSuspensionPeriods?: ExperienceCreditSuspensionPeriod[];
}

export interface PlayingAbilityTestStatus extends StatusWithDates {
  completed: boolean;
  expirationDate?: string | null;
  expired: boolean;
  submissions: {
    id?: string;
    patType: string;
    date: string;
    expirationDate?: string | null;
    expired: boolean;
    name: string | null;
    roundOneScore: number | null;
    roundTwoScore: number | null;
    roundOneId?: string | null;
    roundTwoId?: string | null;
    targetScore: number | null;
    totalScore: number | null;
    result: string;
    additionalStatus: string | null;
    note: string | null;
    slug: string | null;
    tour?: string | null;
    handicap?: number | null;
    document: any | null;
    suspension: any | null;
  }[];
  activeSuspension: {
    startDate: string;
    endDate: string;
  } | null;
}

interface MembershipInterestForm {
  id: string;
  startedAt: string;
  reasonForBecomingMember: string;
  howTheyHeard: string;
}

export interface ProspectProgram {
  id?: string;
  backgroundCheck: BackgroundCheck | null;
  playingAbilityTestStatus: PlayingAbilityTestStatus;
  qualifyingLevel: QualifyingLevel;
  membershipInterestForm: MembershipInterestForm;
  latestAssociateApplication?: LatestAssociateApplication | null;
  eligibleEmployment?: EligibleEmployment | null;
}

export interface CommitteeAssignment {
  committeeName: string;
  position: string;
  startDate: string;
  endDate?: string;
  section?: {
    id: string;
    name: string;
    legacyId?: string | null;
  } | null;
}

export interface EducationCertification {
  startDate: string;
  endDate?: string | null;
  programName: string;
  programDescription: string;
  careerPath: string;
  curriculumVersion: string;
  subscription?: {
    startDate: string;
    endDate?: string | null;
    productId: string;
    expired: boolean;
  };
}

export interface PgmProgramRequirement {
  archived?: boolean;
  requireId: string;
  notes?: string | null;
  remarks?: string | null;
  score?: number | null;
  levelCode: string;
  completionStatus: string;
  completionDate?: string | null;
  isCompleted: boolean;
  isInProgress: boolean;
  createDate?: string | null;
  requirementDefinition?: {
    levelCode: string;
    courseDescription?: string | null;
    courseId?: string | null;
    description: string;
    requireId: string;
    requireType: string;
  } | null;
}

export interface PgmTestDefinition {
  description: string;
  levelCode: string;
  maxScore: number;
  oldTestId?: string | null;
  testId: string;
}

export interface PgmTestScore {
  attemptNumber: number;
  completionStatus: string;
  isFailed: boolean;
  isPassed: boolean;
  isRetake: boolean;
  levelCode: string;
  maxScore: number;
  score: number;
  status: string;
  testDate: string;
  testId: string;
  testDefinition?: PgmTestDefinition | null;
}

export interface EducationPgmProgram {
  archived?: boolean;
  curriculumVersion: string;
  startDate: string;
  endDate?: string | null;
  level: number;
  levelCode: string;
  careerPath?: string | null;
  programType: string;
  completed: boolean;
  completeCode?: string | null;
  remarks?: string | null;
  pgmProgramCompleted?: boolean;
  pgmProgramExpirationDate?: string | null;
  levelExpirationDate?: string | null;
  programDurationYears?: number | null;
  programRequirements?: PgmProgramRequirement[];
  testHistory?: PgmTestScore[];
}

export interface CcmsSuspension {
  id: string;
  code: string;
  startDate: string;
  endDate?: string | null;
  memo?: string | null;
  reasonDescription?: string;
  notes?: CRMNote[];
  membershipSuspensionId?: string | null;
}

export interface PgmSeminarInfo {
  status: string;
  eventId?: string | null;
  eventName?: string | null;
  eventLocation?: string | null;
  eventStartDate?: string | null;
  eventEndDate?: string | null;
  registrationDate?: string | null;
}

export interface PgmRequirementStatus {
  key: string;
  name: string;
  status: string;
  completionDate?: string | null;
  courseName?: string | null;
  seminarInfo?: PgmSeminarInfo | null;
}

export interface PgmLevelStatus {
  key: string;
  name: string;
  numeral: number;
  status: string;
  levelCode?: string | null;
  startDate?: string | null;
  completionDate?: string | null;
  expirationDate?: string | null;
  progressPercent: number;
  requirements: PgmRequirementStatus[];
}

export interface PgmProgressStatus {
  programType?: string | null;
  careerPath?: string | null;
  currentLevelCode?: string | null;
  currentLevelKey?: string | null;
  curriculumVersion?: string | null;
  isGptpSuspended: boolean;
  isSuspended: boolean;
  levels: PgmLevelStatus[];
  mostRecentCompletedLevelKey?: string | null;
  patStatus?: string | null;
  programExpirationDate?: string | null;
  suspensions: CcmsSuspension[];
}

export interface DateOfBirth {
  day?: number;
  month?: number;
  year?: number;
}

export enum Gender {
  Male = "M",
  Female = "F",
  Other = "O"
}

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  suffixName?: string;
  middleName?: string;
  email?: string;
  profilePhoto?: string;
  pgaId?: string;
  membershipSince?: string;
  totalDaysOfMembership?: number;
  /** Active member tenure (election through today, net of suspensions / inactive classifications / terminations). Null when no member anchor. */
  activeDaysOfMembership?: number | null;
  duesBalance?: number;
  mainProgramType?: ProgramType;
  primaryFacility?: { id: string; name: string } | null;
  employments: Employment[];
  backofficeEmployments?: Employment[];
  totalJobApplications?: number;
  totalActiveJobApplications?: number;
  classification?: Classification;
  section?: Section;
  primarySection?: Section;
  sectionByHomeAddress?: Section;
  militaryService?: {
    id: string;
    status: string;
  };
  primarySectionAffiliation?: SectionAffiliation;
  sectionAffiliations?: SectionAffiliation[];
  activeSectionAffiliations?: SectionAffiliation[];
  gender?: Gender;
  ssn?: {
    last4?: string;
    full?: string;
  };
  dob?: DateOfBirth;
  age?: number;
  informalName?: string;
  preferredLastName?: string;
  race?: string;
  spouseName?: string | null;
  directoryOptOut?: boolean | null;
  memberCardFulfillment?: boolean | null;
  spouseCardFulfillment?: boolean | null;
  phoneNumbers?: PhoneNumber[];
  contactAddressId?: string;
  addresses: Address[];
  contactAddress?: Address;
  interactions?: {
    pageInfo: PageInfo;
    nodes: Interaction[];
    totalCount: number;
  };
  latestProgram?: Program;
  bands?: Band[];
  ccmsSuspensions?: CcmsSuspension[];
  prospectProgram?: ProspectProgram;
  accountCreatedAt?: string;
  auth0LastLogin?: string | null;
  auth0LoginCount?: number | null;
  committeeHistory?: CommitteeAssignment[];
  educationLatestCertification?: EducationCertification;
  educationCertificationHistory?: EducationCertification[];
  educationLatestPgmProgram?: EducationPgmProgram;
  educationPgmProgramHistory?: EducationPgmProgram[];
  experienceCredits?: ExperienceCredits;
  pgmProgressStatus?: PgmProgressStatus;
  groupAssignments?: GroupAssignment[];
  recruitmentLead?: RecruitmentLead;
  universityCohort?: { id: string; name: string } | null;
  membershipApplications?: MembershipApplicationSummary[];
  nameChangeDocument?: PersonProfileDocument;
  careerInfo?: {
    hasCompensationProfile?: boolean;
    contactLastModifiedCompensationProfile?: string | null;
    hasJobPreferences?: boolean;
    jobPreferencesLastModified?: string | null;
    currentEmployerContainsGolfDirector?: boolean;
  };
  compensationProfiles?: Array<{
    id: string;
    updatedAt?: string | null;
    currentEmployerContainsGolfDirector?: boolean | null;
    isSnapshot?: boolean;
  }>;
  currentCompensationProfile?: {
    id: string;
    contactLastModifiedDate?: string | null;
    currentEmployerContainsGolfDirector?: boolean | null;
  } | null;
  jobPreferences?: {
    id: string;
    updatedAt?: string | null;
  } | null;
  /** Present while actively routed as a PGM university student (student band → university). */
  university?: {
    id: string;
    legacyId?: string;
    name: string;
  };
  /** Last PGM university attended (survives graduation / withdrawal when active cohort enrollment is cleared). */
  lastAttendedPgmUniversity?: {
    id: string;
    legacyId: string;
    name: string;
  };
  legacyDocuments?: Array<{
    createdAt: string;
    description: string;
    documentContext: string;
    documentType: string;
    fileName: string;
    mimeType: string;
    updatedAt: string;
  }> | null;
  legacyBioPhoto?: {
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null;
}

export interface PersonProfileDocument {
  id: string;
  name: string;
  category: string;
  filename?: string;
  url?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GroupAssignment {
  id: string;
  startedAt: string;
  expiresAt: string;
  group: { id: string; name: string };
}

export enum ProgramTypeName {
  Member = "Member",
  Associate = "Associate",
  Affiliate = "Affiliate",
  Student = "Student",
  Prospect = "Prospect"
}

interface ProgramType {
  id: string;
  name: ProgramTypeName;
}

export interface Classification {
  id: string;
  name: string;
  description: string;
  programType: ProgramType;
}

export interface SectionAffiliation {
  id: string;
  primary: boolean;
  startAt: string;
  endAt: string;
  chapterAffiliations?: SectionChapterAffiliation[];
  chapterAffiliationDescription?: string | null;
  section: Section;
}

export interface SectionChapterAffiliation {
  id: string;
  attributeCode: string;
  chapter: {
    id: string;
    code: string;
    name: string;
  };
  createdAt?: string | null;
  createdBy?: string | null;
}

export interface MembershipProgramClassification {
  id: string;
  startsOn: string;
  endsOn: string;
  classification: Classification;
  sectionAffiliation: SectionAffiliation;
}

export interface DuesRates {
  bypassValidation: boolean;
  classification: string;
  classificationDescription: string;
  finalized: boolean;
  id: string;
  lookupKey: string;
  memberGrade: string;
  name: string;
  notes: string[];
  previousPricing: PreviousPricing[];
  price: string;
  priceCents: number;
  productName: string;
  updatedAt: string;
}

export interface PreviousPricing {
  price: string;
  priceCents: number;
  year: string;
}

import type { ParticipationActivityTypeRef } from "@/pages/activities/activity-types/types";

export interface PDRHistory {
  id: string;
  activityCode: string;
  category: string;
  creditType: string;
  creditsAccepted: number;
  creditsAppliedFor: number;
  description: string;
  startedOn: string;
  submittedAt: string;
  activityParticipation?: {
    id: string;
    title: string;
    canEdit: boolean;
    canDelete: boolean;
    isTypeLevelParticipation: boolean;
    activityType: ParticipationActivityTypeRef;
    activity?: {
      id: string;
      title: string;
      activityType: ParticipationActivityTypeRef;
      managingSection?: {
        id: string;
      } | null;
    } | null;
  };
}

export interface CycleRequirements {
  cohort: string;
  cohortCode: AutoCohort | Cohort | GateCohortCode;
  cycleCode: string;
  cycleStartsOn: string;
  cycleEndsOn: string;
  status: string;
  requirementsEffectiveDate: string;
  requirementsEndDate?: string | null;
  modifiedBy?: string | null;
  requirements: {
    stipulationCode: string;
    label: string;
    earned: number;
    totalRequirement: number;
    cycleRequirement: number;
    deficit: number;
    recoveryThreshold: number;
    recoveryRemaining: number;
  }[];
}

export interface RecruitmentLead {
  id: string;
  zipcode: string | null;
  recruitmentDate: string | null;
  referralSource: string | null;
  entryPoint: string | null;
}

export enum CycleStatus {
  NO_REQUIREMENTS = "no_requirements",
  REQUIREMENTS_NOT_MET = "requirements_not_met",
  REQUIREMENTS_MET = "requirements_met",
  RECOVERING = "recovering"
}

export type AutoCohort = "SD" | "IN" | "NO" | "NT";
export type Cohort = "A" | "D" | "OR";
export type GateCohortCode = "LM" | "RI";

export type PdrGateObligationStipulation = {
  stipulationCode: string;
  label: string;
  required: number;
  earned: number;
  remaining: number;
};

export type PdrGateCalculationBucket = {
  stipulationCode: string;
  label: string;
  priorNormalRequirement: number;
  prorationCredit: number;
  earnedAtEvent: number;
  deficitAdded: number;
  frozenRequirement: number;
};

export type PdrGateCalculation = {
  eventDate: string;
  priorCohortCode: string;
  priorCohortDescription: string;
  priorClassification: string;
  priorCycleCode: string;
  buckets: PdrGateCalculationBucket[];
};

export type PdrGateObligation = {
  cohortCode: GateCohortCode;
  originCycleCode?: string | null;
  eventDate?: string | null;
  satisfied: boolean;
  stipulations: PdrGateObligationStipulation[];
  calculation?: PdrGateCalculation | null;
};

export const TaskStageValues = {
  OPEN: "OPEN",
  COMPLETE: "COMPLETE",
  NOT_DOING: "NOT_DOING"
} as const;

export type TaskStage = (typeof TaskStageValues)[keyof typeof TaskStageValues];

export const TaskVisibilityValues = {
  PUBLIC: "PUBLIC",
  PRIVATE: "PRIVATE"
} as const;

export type TaskVisibility = (typeof TaskVisibilityValues)[keyof typeof TaskVisibilityValues];

interface CRMTaskNoteReference {
  __typename: "CRMNote";
  id: string;
  title?: string;
  displayTitle?: string;
  notableId?: string;
  notableType?: string;
  notableName?: string | null;
  personPgaId?: string;
  facilityId?: string | null;
  eventSlug?: string | null;
  universityLegacyId?: string | null;
  authorName?: string | null;
  createdAt: string | null;
  updatedAt?: string | null;
}

interface CRMTaskInteractionReference {
  __typename: "CRMInteraction";
  id: string;
  relatedTo?: string;
  mode?: InteractionMode;
  categories?: string[];
  owner?: { name: string };
  participants?: { pgaId: string; firstName?: string; lastName?: string }[];
  createdAt: string | null;
  lastModifiedDate?: string | null;
}

interface CRMTaskApplicationReference {
  __typename: "BackofficeMembershipApplication";
  id: string;
  applicationType?: string;
  status?: string;
  person?: { pgaId: string; displayName: string };
  createdAt: string | null;
}

interface CRMTaskOrderReference {
  __typename: "CommerceOrder";
  id: string;
  description?: string;
  status?: string;
  dueDate?: string | null;
  balance?: number;
  person?: { pgaId: string; firstName?: string; lastName?: string; displayName?: string };
  updatedAt?: string | null;
}

interface CRMTaskPaymentReference {
  __typename: "CommercePayment";
  id: string;
  amountCents?: number;
  type?: string;
  status?: string;
  paidAt?: string | null;
  payer?: { id: string; name: string };
}

interface CRMTaskPersonReference {
  __typename: "CRMPerson";
  id: string;
  pgaId?: string | null;
  firstName?: string;
  lastName?: string;
  displayName?: string | null;
  profilePhoto?: string | null;
  email?: string | null;
}

interface CRMTaskEventReference {
  __typename: "CRMEvent";
  id: string;
  title: string;
  slug: string;
  status?: string;
  startsAt?: string | null;
  endsAt?: string | null;
}

interface CRMTaskFacilityReference {
  __typename: "CRMTaskFacility";
  id: string;
  name: string;
}

interface CRMTaskCareerPlanReference {
  __typename: "BackofficeCareerPlan";
  id: string;
  title?: string | null;
  status?: string;
  updatedAt?: string | null;
  pgaId?: string | null;
}

export type CRMTaskReference =
  | CRMTaskNoteReference
  | CRMTaskInteractionReference
  | CRMTaskApplicationReference
  | CRMTaskOrderReference
  | CRMTaskPaymentReference
  | CRMTaskPersonReference
  | CRMTaskEventReference
  | CRMTaskFacilityReference
  | CRMTaskCareerPlanReference;

export interface CRMTaskChangelog {
  id: number;
  description: string;
  timestamp: string;
  modifiedBy: string;
}

export interface CRMTaskUser {
  id: string;
  name: string | null;
  email: string | null;
  photo: string | null;
}

export interface CRMTaskComment {
  id: string;
  content: string;
  authorName: string | null;
  author: CRMTaskUser;
  createdAt: string;
  updatedAt: string;
}

export interface CRMTaskTeamTag {
  source: string;
  name: string;
}

export interface CRMTask {
  id: string;
  name: string;
  description: string;
  stage: TaskStage;
  visibility: TaskVisibility;
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  author: CRMTaskUser;
  assignedTo: CRMTaskUser | null;
  teamTags?: CRMTaskTeamTag[];
  assignedToDepartment: string | null;
  authorDepartment: string | null;
  references?: CRMTaskReference[];
  changelogs?: CRMTaskChangelog[];
  comments?: CRMTaskComment[];
  commentsCount?: number;
}
